import React from "react";
import { ChatInput } from "@/components/agent/chat/ChatInput";
import { useChatContext } from "@/hooks/useChatContext";
import { ThankYouScreen } from "@/components/agent/chat/ThankYouScreen";
import { ChatMessages } from "@/components/agent/chat/ChatMessages";
import { ConversationStarters } from "@/components/agent/chat/ConversationStarters";
import { Tables } from "@/types/database.types";
import { AgentChatUser } from "@/components/agent/chat/types";
import { PublicAgentNavBar } from "@/components/agent/chat/PublicAgentNavBar";
import { FollowUpSuggestions } from "@/components/agent/chat/FollowUpSuggestions";
import { parseRichContent } from "@/utils/richContentParser";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { filterRichContentByCapabilities } from "@/utils/richContentParser";
import { sanitizeBrandColor } from "@/lib/utils";

// Define custom organization type for UI-specific properties
type OrganizationWithUI = Tables<"organizations"> & {
  tagline?: string;
};

interface DesktopChatLayoutProps {
  agent: Tables<"agents">;
  organization?: OrganizationWithUI;
  brandColor: string;
  userInfo?: AgentChatUser;
  isConversationCompleted?: boolean;
  thankYouMessage?: string;
  onStartNew?: () => void;
  onResumeConversation?: () => void;
  hideHeader?: boolean;
}

export const DesktopChatLayout: React.FC<DesktopChatLayoutProps> = ({
  agent,
  organization,
  brandColor,
  userInfo,
  isConversationCompleted = false,
  thankYouMessage = "",
  onStartNew,
  onResumeConversation,
  hideHeader = false,
}) => {
  const {
    chatMessages,
    isLoading,
    handleSendMessage,
    handleResetConversation,
    canResetConversation,
    handleCloseConversation,
    textColor,
    //conversationState,
  } = useChatContext();

  const [searchParams] = useSearchParams();
  const hideNavBar = searchParams.get("hideNavBar") === "true";
  const isEmbedded =
    searchParams.get("embed") === "true" ||
    searchParams.get("embedBubble") === "true";

  // Use first name, unless it's an anonymous placeholder
  const rawFirstName = userInfo?.name?.split(" ")[0] || "";
  const isAnonymous = rawFirstName.toLowerCase().startsWith("anonymous");
  const firstName = isAnonymous || rawFirstName === "" ? "" : rawFirstName;

  // Show conversation starters if we're at the initial state
  const showConversationStarters =
    chatMessages.length === 1 && agent?.conversation_starters?.length > 0;

  // Get the last agent message for follow-up suggestions
  const lastAgentMessage =
    chatMessages.length > 0
      ? [...chatMessages].reverse().find((m) => m.type === "agent")
      : null;

  // Extract follow-up suggestions and scheduling widgets
  const parsedContent = lastAgentMessage
    ? parseRichContent(lastAgentMessage.content)
    : {
        cleanContent: "",
        insideContent: [],
        bottomContent: [],
        belowContent: [],
      };
  const richContent = filterRichContentByCapabilities(parsedContent, agent);

  // Check if there's a scheduling widget
  const hasSchedulingWidget = richContent.belowContent?.some(
    (item) => item.type === "SCHEDULING" || item.type === "CALENDLY",
  );

  // Combine all suggestions
  const followUpSuggestions = [
    ...richContent.bottomContent
      .filter((item) => item.type === "FOLLOWUP")
      .map((item) => item.content),
    ...(hasSchedulingWidget ? ["I've booked a slot"] : []),
  ];

  // Only show follow-ups if they exist and we're not showing conversation starters
  const showFollowUps =
    followUpSuggestions.length > 0 && !showConversationStarters;

  // Always apply the same background system as public chat page (2-layer system) - fix hex opacity and sanitize brand color
  const containerStyle: React.CSSProperties = {
    background: `linear-gradient(to bottom, ${sanitizeBrandColor(organization?.brand_color || "#3b82f6")}26 0%, #ffffff 100%)`,
    minHeight: "100dvh",
    height: "auto",
  };

  const hasCustomBackground =
    organization?.chat_background &&
    organization.chat_background !== "default" &&
    organization.chat_background !== "Default - Linear Background";

  const customBackgroundUrl = hasCustomBackground
    ? supabase.storage
        .from("image_library")
        .getPublicUrl(`chat_backgrounds/${organization.chat_background}`).data
        .publicUrl
    : null;

  return (
    <div
      className={`flex flex-col ${isEmbedded ? "h-full" : "h-screen"} w-full overflow-hidden`}
    >
      {/* Navbar - only shown if not explicitly hidden */}
      {!hideNavBar && !hideHeader && (
        <PublicAgentNavBar
          organization={organization}
          agent={agent}
          brandColor={brandColor}
        />
      )}

      {/* Main Chat Area */}
      <div
        className="flex-1 flex flex-col overflow-hidden relative"
        style={containerStyle}
      >
        {/* Custom Background Overlay */}
        {hasCustomBackground && customBackgroundUrl && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: organization?.brand_color || "#3b82f6",
              maskImage: `url(${customBackgroundUrl})`,
              WebkitMaskImage: `url(${customBackgroundUrl})`,
              maskSize: "cover",
              WebkitMaskSize: "cover",
              maskPosition: "center center",
              WebkitMaskPosition: "center center",
              maskRepeat: "no-repeat",
              WebkitMaskRepeat: "no-repeat",
              backgroundPosition: "center center",
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
              opacity: 0.06,
              zIndex: 0,
              pointerEvents: "none",
            }}
          />
        )}

        {/* Chat Messages */}
        <div className="flex-1 flex flex-col overflow-hidden bg-transparent relative z-[1]">
          {isConversationCompleted ? (
            <ThankYouScreen
              message={thankYouMessage}
              brandColor={brandColor}
              textColor={textColor}
              onStartNew={onStartNew}
              onResumeConversation={onResumeConversation}
            />
          ) : (
            <>
              {/* Scrollable chat area */}
              <div className="flex-1 overflow-y-auto">
                {/* Welcome section */}
                <div>
                  <div className="max-w-[770px] mx-auto p-8">
                    {/* Spacing div to maintain gap between nav and banner */}
                    <div className="text-center mb-8">
                      <div className="p-4 rounded mt-4"></div>
                    </div>

                    {/* Banner - show if organization has a banner image with top margin from nav bar */}
                    {organization?.banner_url && (
                      <div className="w-full mb-5 mt-8">
                        <img
                          src={organization.banner_url}
                          alt="Organization Banner"
                          className="w-full rounded-[7px] h-auto object-cover max-h-[140px]"
                        />
                      </div>
                    )}

                    <div className="text-center">
                      <h2 className="text-2xl font-bold flex items-center justify-center gap-2 mb-1">
                        <span className="text-gray-800">Hi </span>
                        {firstName ? (
                          <>
                            <span style={{ color: brandColor }}>
                              {firstName}
                            </span>
                            <span className="text-xl">👋</span>
                          </>
                        ) : (
                          <span style={{ color: brandColor }}>there 👋</span>
                        )}
                      </h2>
                    </div>
                  </div>
                </div>

                {/* Chat messages */}
                <div className="p-4">
                  <div className="max-w-[770px] mx-auto">
                    <ChatMessages
                      chatMessages={chatMessages}
                      isLoading={isLoading}
                      agent={agent}
                      organization={organization}
                      brandColor={brandColor}
                      textColor={textColor}
                      isPublic={true}
                      isMobile={false}
                      onSendMessage={handleSendMessage}
                      onCloseConversation={handleCloseConversation}
                    />
                  </div>
                </div>
              </div>

              {/* Bottom chat controls */}
              <div className="flex-none bg-transparent">
                {/* Conversation Starters - right aligned, no full-width border */}
                {showConversationStarters && (
                  <div className="max-w-[770px] mx-auto p-4">
                    <div className="flex justify-end">
                      <ConversationStarters
                        starters={agent.conversation_starters}
                        onStarterClick={handleSendMessage}
                        brandColor={brandColor}
                      />
                    </div>
                  </div>
                )}

                {/* Follow-up suggestions - right aligned, no full-width border */}
                {showFollowUps && (
                  <div className="max-w-[770px] mx-auto p-4">
                    <div className="flex justify-end">
                      <FollowUpSuggestions
                        suggestions={followUpSuggestions}
                        onSuggestionClick={handleSendMessage}
                        brandColor={brandColor}
                      />
                    </div>
                  </div>
                )}

                {/* Input Area */}
                <div className="p-4 bg-transparent">
                  <div className="max-w-[770px] mx-auto">
                    <ChatInput
                      onSendMessage={handleSendMessage}
                      isLoading={isLoading}
                      brandColor={brandColor}
                      isPublic={true}
                      isMobile={false}
                      onReset={handleResetConversation}
                      canReset={canResetConversation}
                      isDisabled={false}
                      placeholder="Type your message..."
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      {/* Legal disclaimer removed as requested */}

      {/* Powered by Hubflo - only show if branding is enabled */}
      {organization?.display_hubflo_branding !== false && (
        <div className="px-4 py-2 text-center bg-gray-100 border-t">
          <a
            href="https://hubflo.com/agents"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center text-gray-400 text-xs hover:underline transition-all"
          >
            <div className="w-4 h-4 rounded-full overflow-hidden mr-1.5 flex-shrink-0">
              <img
                src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/image_library/public_images/logo%20squared%20hubflo.webp`}
                alt="Hubflo Logo"
                className="w-full h-full object-cover"
              />
            </div>
            Powered by Hubflo
          </a>
        </div>
      )}
    </div>
  );
};
